<template>
    <div class="dbconsole-work">
        <div class="dbconsole-toolbar">
            <el-select v-model="databaseModel" class="dbconsole-toolbar__db" size="small"
                placeholder="选择数据库" filterable allow-create default-first-option :loading="dbLoading">
                <el-option v-for="db in databases" :key="db" :label="db" :value="db" />
            </el-select>
            <div class="dbconsole-toolbar__ops">
                <el-button size="small" type="primary" :loading="block.loading" @click="$emit('run')">
                    <el-icon class="el-icon--left">
                        <CaretRight />
                    </el-icon>
                    执行全部
                </el-button>
                <el-button size="small" type="primary" plain :loading="block.loading" @click="runSelected">
                    <el-icon class="el-icon--left">
                        <Select />
                    </el-icon>
                    执行选中
                </el-button>
                <el-button size="small" type="warning" @click="$emit('save')">
                    <el-icon class="el-icon--left">
                        <Star />
                    </el-icon>
                    保存
                </el-button>
                <el-button size="small" text @click="$emit('tables')">
                    <el-icon class="el-icon--left">
                        <Grid />
                    </el-icon>
                    表
                </el-button>
                <el-button size="small" text @click="copySql">
                    <el-icon class="el-icon--left">
                        <DocumentCopy />
                    </el-icon>
                    复制
                </el-button>
                <el-button size="small" text @click="formatSqlText">格式化</el-button>
            </div>
        </div>

        <el-splitter layout="vertical" class="dbconsole-split" @resize-end="$emit('split-resize')">
            <el-splitter-panel size="50%" :min="120">
                <div class="dbconsole-editor-pane">
                    <sql-editor ref="sqlEditor" v-model="sqlTextModel" :mode="sqlMode"
                        :tables="tables" :table-comments="tableComments" :columns="columns"
                        :databases="databases" :tables-by-db="tablesByDb"
                        :table-comments-by-db="tableCommentsByDb"
                        placeholder="在此输入 SQL（自动提示表名，支持 库名.表名；Ctrl/Cmd + Enter：有选中则执行选中，否则执行全部）"
                        @run="selected => $emit('run-shortcut', selected)"
                        @need-columns="tables => $emit('need-columns', tables)"
                        @need-tables="db => $emit('need-tables', db)" />
                </div>
            </el-splitter-panel>
            <el-splitter-panel :min="120">
                <div class="dbconsole-result-pane">
                    <div v-if="!block.results.length" class="dbconsole-result-empty">
                        执行 SQL 后在此查看结果
                    </div>
                    <template v-else-if="block.results.length === 1">
                            <db-result-panel :key="block.uid + '-r0'"
                            :result="block.results[0].result"
                            :error="block.results[0].error"
                            :loading="block.results[0].loading"
                            :sql="block.results[0].sql"
                            :column-meta="columns"
                            :can-write="canWrite"
                            :commit-handler="payload => handleCommit(0, payload)"
                            @favorite="rows => $emit('favorite', block, rows, block.results[0].result, block.results[0].sql)" />
                    </template>
                    <el-tabs v-else v-model="activeResultModel" class="dbconsole-result-tabs">
                        <el-tab-pane v-for="(st, si) in block.results" :key="st.id" :name="st.id" :lazy="true">
                            <template #label>
                                <span class="dbconsole-tab">
                                    <span class="dbconsole-tab__no">#{{ si + 1 }}</span>
                                    <span class="dbconsole-tab__sum" :class="'is-' + st.status">
                                        {{ st.summary || '待执行' }}</span>
                                </span>
                            </template>
                            <db-result-panel :result="st.result" :error="st.error" :loading="st.loading"
                                :sql="st.sql" :column-meta="columns" :can-write="canWrite"
                                :commit-handler="payload => handleCommit(si, payload)"
                                @favorite="rows => $emit('favorite', block, rows, st.result, st.sql)" />
                        </el-tab-pane>
                    </el-tabs>
                </div>
            </el-splitter-panel>
        </el-splitter>
    </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { format as formatSql } from 'sql-formatter';
import DbResultPanel from '../DbResultPanel.vue';
import SqlEditor from '../SqlEditor.vue';

export default {
    name: 'DbQueryWorkbench',
    components: { DbResultPanel, SqlEditor },
    props: {
        block: { type: Object, required: true },
        sqlText: { type: String, default: '' },
        database: { type: String, default: '' },
        activeResult: { type: String, default: '' },
        conn: { type: Object, default: () => ({}) },
        sqlMode: { type: String, default: 'text/x-sql' },
        databases: { type: Array, default: () => [] },
        dbLoading: { type: Boolean, default: false },
        tables: { type: Array, default: () => [] },
        tableComments: { type: Object, default: null },
        columns: { type: Object, default: null },
        tablesByDb: { type: Object, default: null },
        tableCommentsByDb: { type: Object, default: null },
        canWrite: { type: Boolean, default: false },
        commitHandler: { type: Function, default: null }
    },
    emits: ['run', 'run-shortcut', 'save', 'tables', 'need-columns', 'need-tables', 'split-resize', 'favorite', 'execute', 'update:sqlText', 'update:database', 'update:activeResult'],
    computed: {
        sqlTextModel: {
            get() { return this.sqlText; },
            set(v) { this.$emit('update:sqlText', v); }
        },
        databaseModel: {
            get() { return this.database; },
            set(v) { this.$emit('update:database', v); }
        },
        activeResultModel: {
            get() { return this.activeResult; },
            set(v) { this.$emit('update:activeResult', v); }
        }
    },
    methods: {
        handleCommit(index, payload) {
            if (!this.commitHandler) return Promise.resolve(false);
            return this.commitHandler(this.block, index, payload);
        },
        refreshEditor() {
            const ed = this.$refs.sqlEditor;
            if (ed && typeof ed.refresh === 'function') ed.refresh();
        },
        getSelectedText() {
            const ed = this.$refs.sqlEditor;
            return ed && typeof ed.getSelectedText === 'function' ? ed.getSelectedText() : '';
        },
        runSelected() {
            const selected = this.getSelectedText();
            if (!selected || !selected.trim()) {
                ElMessage({ message: '请先在编辑器中选中要执行的 SQL', type: 'warning', duration: 2000 });
                return;
            }
            this.$emit('execute', selected);
        },
        copySql() {
            const text = (this.block && this.block.sqlText) || '';
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
                });
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
            }
        },
        formatSqlText() {
            const sql = ((this.block && this.block.sqlText) || '').trim();
            if (!sql) {
                ElMessage({ message: '请输入 SQL', type: 'warning', duration: 2000 });
                return;
            }
            try {
                this.sqlTextModel = formatSql(sql, {
                    language: this.conn && this.conn.dbType === 'mysql' ? 'mysql' : 'postgresql',
                    keywordCase: 'upper'
                });
            } catch (e) {
                ElMessage({ message: '格式化失败：' + (e && e.message ? e.message : '语法无法解析'), type: 'error', duration: 3000 });
            }
        }
    }
};
</script>

<style>
.dbconsole-work {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.dbconsole-toolbar {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #f3f7fd 0%, #ffffff 100%);
}

.dbconsole-toolbar__db {
    width: 180px;
}

.dbconsole-toolbar__ops {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.dbconsole-split {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbconsole-split .el-splitter-panel {
    overflow: hidden;
    min-height: 0;
}

.dbconsole-split .el-splitter-bar {
    z-index: 4;
}

/* 组件 bar 高度为 0，拖动手柄绝对定位且自带 translate(-50%)。
   分隔线必须用 width:100% + 居中，不能再用 left:0/right:0，否则会再左移半幅。 */
.dbconsole-split .el-splitter-bar__dragger-vertical::before,
.dbconsole-split .el-splitter-bar__dragger-vertical::after {
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
}

.dbconsole-split .el-splitter-bar__dragger-vertical::before {
    width: 100%;
    height: 1px;
    background-color: #dbe3ef;
}

.dbconsole-split .el-splitter-bar__dragger-vertical::after {
    width: 36px;
    height: 4px;
    margin: 0;
    border-radius: 999px;
    background-color: #c5d0e0;
}

.dbconsole-split .el-splitter-bar__dragger-vertical:hover::before {
    background-color: var(--el-color-primary-light-5, #97b7f6);
}

.dbconsole-split .el-splitter-bar__dragger-vertical:hover::after {
    background-color: var(--el-color-primary, #2f6fed);
}

.dbconsole-editor-pane,
.dbconsole-result-pane {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.dbconsole-editor-pane {
    padding: 8px 12px;
    box-sizing: border-box;
}

.dbconsole-editor-pane .sql-editor {
    flex: 1 1 auto;
    min-height: 0;
}

.dbconsole-result-pane {
    padding: 8px 12px 12px;
    box-sizing: border-box;
    isolation: isolate;
}

.dbconsole-result-pane > .dbresult,
.dbconsole-result-pane > .dbconsole-result-tabs {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbconsole-result-empty {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
    color: #aab4c4;
    font-size: 13px;
}

.dbconsole-result-tabs {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbconsole-result-tabs .el-tabs__header {
    flex: 0 0 auto;
    margin: 0 0 8px;
}

.dbconsole-result-tabs .el-tabs__item {
    height: 30px;
    line-height: 30px;
    padding: 0 10px;
    font-size: 12px;
}

.dbconsole-result-tabs .el-tabs__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.dbconsole-result-tabs .el-tab-pane {
    height: 100%;
}

.dbconsole-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dbconsole-tab__no {
    color: #7d8ba1;
    font-weight: 600;
}

.dbconsole-tab__sum.is-done {
    color: #4c9a6b;
}

.dbconsole-tab__sum.is-error {
    color: #f56c6c;
}

.dbconsole-tab__sum.is-running {
    color: #2f6fed;
}

.dbconsole-tab__sum.is-skipped,
.dbconsole-tab__sum.is-pending {
    color: #aab4c4;
}
</style>
